import { ChangeEvent, useMemo, useRef, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { HiOutlineArrowUpTray } from "react-icons/hi2";
import { useImageUploadToS3API } from "@/app/hooks/api_hooks/coupon/useImageUploadToS3API";
import {
  secondaryButtonClassName,
  textInputClassName,
} from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import { GiftThumbnail } from "../components";
import { GIFT_TIME_UNIT_ITEMS, IGiftFormFields, toSelectItems } from "../interface";
import { FormField, GiftSelectField, InlineSwitchField, SectionCard, errorBorderClassName } from "./fields";
import { validateImageUrl, validatePositiveInteger } from "./validators";

const PlatformSelect = ({
  name,
  label,
  allowedPlatforms,
  isFormatLoading,
}: {
  name: "add_platform" | "avail_platform" | "claim_platform";
  label: string;
  allowedPlatforms: string[];
  isFormatLoading: boolean;
}) => {
  const items = useMemo(() => toSelectItems(allowedPlatforms), [allowedPlatforms]);

  return (
    <GiftSelectField
      name={name}
      label={label}
      required
      items={items}
      isLoading={isFormatLoading}
      placeholder="Select platform"
    />
  );
};

const GiftImageField = () => {
  const { control, register, setValue } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name: "gift_image" });
  const [giftImage, giftName] = useWatch({ control, name: ["gift_image", "gift_name"] });
  const { execute: uploadImage } = useImageUploadToS3API();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setIsUploading(true);
    const result = await uploadImage(file);
    setIsUploading(false);
    if (result?.status && result.message) {
      setValue("gift_image", result.message, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <FormField label="Gift Image" htmlFor="gift_image" required error={errors.gift_image?.message}>
      <div className="flex items-start gap-3">
        <GiftThumbnail
          imageUrl={giftImage}
          alt={giftName || "Gift image"}
          className="h-20 w-20 bg-white"
          iconClassName="h-8 w-8"
        />
        <div className="min-w-0 flex-1 space-y-2">
          <input
            id="gift_image"
            type="url"
            placeholder="https://…/chew-toy.png"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.gift_image))}`}
            {...register("gift_image", { validate: validateImageUrl })}
          />
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={`flex items-center gap-1.5 ${secondaryButtonClassName} disabled:opacity-60`}
          >
            <HiOutlineArrowUpTray className="h-4 w-4" />
            {isUploading ? "Uploading…" : "Upload image"}
          </button>
        </div>
      </div>
    </FormField>
  );
};

export const DisplayCard = ({
  allowedPlatforms,
  isFormatLoading,
}: {
  allowedPlatforms: string[];
  isFormatLoading: boolean;
}) => {
  const { control, register } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control });

  return (
    <SectionCard
      title="Display & Platform"
      description="What the customer sees in the cart and where the gift applies."
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField label="Heading" htmlFor="heading" required error={errors.heading?.message}>
          <input
            id="heading"
            type="text"
            placeholder="Free Product for your pet"
            className={`${textInputClassName} ${errorBorderClassName(Boolean(errors.heading))}`}
            {...register("heading", { validate: (value) => Boolean(value.trim()) || "Heading is required." })}
          />
        </FormField>
        <FormField label="Sub Heading" htmlFor="sub_heading">
          <input
            id="sub_heading"
            type="text"
            placeholder="Avail in this order"
            className={textInputClassName}
            {...register("sub_heading")}
          />
        </FormField>
        <div className="md:col-span-2">
          <GiftImageField />
        </div>
        <PlatformSelect
          name="add_platform"
          label="Add Platform"
          allowedPlatforms={allowedPlatforms}
          isFormatLoading={isFormatLoading}
        />
        <PlatformSelect
          name="avail_platform"
          label="Avail Platform"
          allowedPlatforms={allowedPlatforms}
          isFormatLoading={isFormatLoading}
        />
      </div>
    </SectionCard>
  );
};

type DurationFieldName = "allowed_claim_time" | "allowed_avail_time";

/** A number plus a Days/Hours/Minutes unit; formUtils sends it to the backend in seconds. Empty = no limit. */
const DurationField = ({ name, label }: { name: DurationFieldName; label: string }) => {
  const { control, register } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name });

  return (
    <FormField label={label} htmlFor={name} error={errors[name]?.message}>
      {/* 104px fits "Minutes" plus the dropdown arrow. */}
      <div className="grid grid-cols-[1fr_104px] gap-2">
        <input
          id={name}
          type="number"
          min={1}
          placeholder="No limit"
          className={`${textInputClassName} ${errorBorderClassName(Boolean(errors[name]))}`}
          {...register(name, { validate: (value) => !value.trim() || validatePositiveInteger(label)(value) })}
        />
        <GiftSelectField name={`${name}_unit`} label="" items={GIFT_TIME_UNIT_ITEMS} />
      </div>
    </FormField>
  );
};

export const ClaimConditionsCard = ({
  allowedPlatforms,
  isFormatLoading,
}: {
  allowedPlatforms: string[];
  isFormatLoading: boolean;
}) => {
  const { control, setValue } = useFormContext<IGiftFormFields>();
  const isToBeClaimed = useWatch({ control, name: "tobe_claimed" });

  return (
    <SectionCard title="Claim Conditions">
      {/* One row on wide screens (the Yes/No switch only needs a narrow column); 2×2 on tablets, stacked on phones. */}
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-[160px_1fr_1fr_1fr]">
        <InlineSwitchField
          label="To Be Claimed"
          checked={isToBeClaimed}
          onChange={(next) => setValue("tobe_claimed", next, { shouldDirty: true })}
        />
        <PlatformSelect
          name="claim_platform"
          label="Claim Platform"
          allowedPlatforms={allowedPlatforms}
          isFormatLoading={isFormatLoading}
        />
        <DurationField name="allowed_claim_time" label="Allowed Claim Time" />
        <DurationField name="allowed_avail_time" label="Allowed Avail Time" />
      </div>
    </SectionCard>
  );
};
